import { useEffect, useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTaskStore } from '../store/taskStore'
import { useAuthStore } from '../store/authStore'
import TaskCard from '../components/TaskCard'
import OnboardingCarousel from '../components/OnboardingCarousel'
import Spinner from '../components/common/Spinner'

const LEVEL_XP = [0, 100, 300, 700, 1500]

export default function TaskListPage() {
  const { t } = useTranslation('worker')
  const { availableTasks, myAssignments, submissions, fetchAvailableTasks, fetchMyAssignments, isLoading } =
    useTaskStore()
  const { stats, fetchStats } = useAuthStore()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'all' | 'mine'>('all')
  const [showOnboarding, setShowOnboarding] = useState(false)

  useEffect(() => {
    fetchAvailableTasks()
    fetchMyAssignments()
    fetchStats()
  }, [fetchAvailableTasks, fetchMyAssignments, fetchStats])

  useEffect(() => {
    if (stats !== null && stats.coins_total_earned === 0 && !localStorage.getItem('crowd_onboarding_seen')) {
      setShowOnboarding(true)
    }
  }, [stats])

  const activeAssignments = myAssignments.filter((a) =>
    ['claimed', 'in_progress', 'submitted'].includes(a.status)
  )
  const completedAssignments = myAssignments.filter((a) =>
    ['completed', 'abandoned'].includes(a.status)
  )

  const claimedTaskIds = new Set(
    myAssignments.filter((a) => a.status !== 'abandoned').map((a) => a.task_id)
  )
  const filteredAvailableTasks = availableTasks.filter((t) => !claimedTaskIds.has(t.id))

  // XP progress within current level
  const xpInLevel = stats ? stats.xp - stats.current_level_xp : 0
  const xpNeeded = stats?.next_level_xp ? stats.next_level_xp - stats.current_level_xp : 0
  const xpProgress = xpNeeded > 0 ? Math.min(xpInLevel / xpNeeded, 1) : 1
  const nextLevelNum = stats ? stats.level + 1 : 2
  const nextLevelName = t(`levels.${nextLevelNum}.name`, {
    ns: 'gamification',
    defaultValue: t('levels.5.name', { ns: 'gamification' }),
  })

  const handleDismissOnboarding = () => {
    localStorage.setItem('crowd_onboarding_seen', '1')
    setShowOnboarding(false)
  }

  // Hero CTA content
  const firstActive = activeAssignments[0]
  const firstAvailable = filteredAvailableTasks[0]

  return (
    <div className="min-h-screen" style={{ background: 'var(--app-page-bg)' }}>
      {showOnboarding && <OnboardingCarousel onDismiss={handleDismissOnboarding} />}

      {/* ── Header ───────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 pt-5 pb-4"
        style={{ background: 'var(--app-card-bg)' }}
      >
        {/* App icon */}
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
        >
          🏪
        </div>
        <h1 className="font-bold text-xl flex-1" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('taskList.title')}
        </h1>
        {/* Coin badge */}
        <div
          className="flex flex-col items-center px-3 py-1.5 rounded-2xl border-2 cursor-pointer"
          style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
          onClick={() => navigate('/profile')}
        >
          <span className="font-bold text-sm leading-tight">
            🪙 {stats?.coins_balance ?? 0}
          </span>
          {(stats?.coins_pending ?? 0) > 0 && (
            <span className="text-[10px] font-semibold leading-tight opacity-75">
              {t('taskList.pendingCoins', { count: stats!.coins_pending })}
            </span>
          )}
        </div>
        {/* Avatar */}
        <button
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--app-page-bg)' }}
          onClick={() => navigate('/profile')}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="4" fill="var(--tg-theme-hint-color)" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="var(--tg-theme-hint-color)" />
          </svg>
        </button>
      </div>

      <div className="px-4 pt-4 pb-6 space-y-4">
        {/* ── Hero CTA card ──────────────────────────────────── */}
        {firstActive ? (
          <button
            className="w-full rounded-3xl p-5 text-left active:opacity-90 transition-opacity overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 60%, #6d28d9 100%)',
              color: 'white',
            }}
            onClick={() => navigate(`/tasks/${firstActive.task_id}`)}
          >
            <div className="absolute -right-4 -top-4 rounded-full opacity-20" style={{ width: 120, height: 120, background: 'white' }} />
            <div className="absolute -right-10 top-12 rounded-full opacity-10" style={{ width: 160, height: 160, background: 'white' }} />
            <p className="text-sm opacity-80 mb-1">{t('taskList.continueTask')}</p>
            <p className="font-bold text-xl leading-tight">{firstActive.task.title}</p>
            <p className="text-sm opacity-75 mt-1">
              {t('taskList.itemsRemaining', {
                count: firstActive.task.items.length - firstActive.items_found - firstActive.items_not_found,
              })}
            </p>
            {(() => {
              const remaining = firstActive.task.items.length - firstActive.items_found - firstActive.items_not_found
              const reward = firstActive.task.reward_per_item_coins
              return reward && remaining > 0 ? (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-sm font-semibold"
                  style={{ background: 'rgba(251,191,36,0.25)', color: '#fde68a' }}
                >
                  {t('taskList.coinsRemaining', { coins: reward * remaining })}
                </div>
              ) : null
            })()}
            <div
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold text-sm"
              style={{ background: 'white', color: '#3b82f6' }}
            >
              {t('taskList.continue')}
            </div>
          </button>
        ) : firstAvailable ? (
          <button
            className="w-full rounded-3xl p-5 text-left active:opacity-90 transition-opacity overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #4f46e5 60%, #6d28d9 100%)',
              color: 'white',
            }}
            onClick={() => navigate(`/tasks/${firstAvailable.id}`)}
          >
            <div className="absolute -right-4 -top-4 rounded-full opacity-20" style={{ width: 120, height: 120, background: 'white' }} />
            <div className="absolute -right-10 top-12 rounded-full opacity-10" style={{ width: 160, height: 160, background: 'white' }} />
            <p className="text-sm opacity-80 mb-1">{t('taskList.readyToEarn')}</p>
            <p className="font-bold text-xl leading-tight">{t('taskList.firstTaskAwaits')}</p>
            {firstAvailable.target_location_name && (
              <p className="text-sm opacity-80 mt-1.5 flex items-center gap-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="white" opacity="0.9"/>
                </svg>
                {firstAvailable.target_location_name}
              </p>
            )}
            {firstAvailable.reward_per_item_coins != null && firstAvailable.items.length > 0 && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(251,191,36,0.25)', color: '#fde68a' }}
              >
                {t('taskList.earnUpTo', { coins: firstAvailable.reward_per_item_coins * firstAvailable.items.length })}
              </div>
            )}
            <div
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold text-sm"
              style={{ background: 'white', color: '#3b82f6' }}
            >
              {t('taskList.start')}
            </div>
          </button>
        ) : null}

        {/* ── Compact stats row ─────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              icon: stats?.level_emoji ?? '🌱',
              value: String(stats?.level ?? 1),
              label: t('taskList.stats.level'),
            },
            {
              icon: (stats?.streak_days ?? 0) >= 3 ? '🔥' : '📅',
              value: `${stats?.streak_days ?? 0}d`,
              label: t('taskList.stats.streak'),
            },
            { icon: '✅', value: `${stats?.approval_rate ?? 0}%`, label: t('taskList.stats.approval') },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl py-3 flex flex-col items-center"
              style={{ background: 'var(--app-card-bg)' }}
            >
              <span style={{ fontSize: 26 }}>{item.icon}</span>
              <p className="font-bold text-xl mt-1" style={{ color: 'var(--tg-theme-text-color)' }}>
                {item.value}
              </p>
              <p className="text-xs opacity-40" style={{ color: 'var(--tg-theme-text-color)' }}>
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* ── Explore & Contribute ──────────────────────────── */}
        <div>
          <h2 className="font-bold text-lg mb-3" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('taskList.explore')}
          </h2>
          <div
            className="rounded-2xl overflow-hidden"
            style={{ background: 'var(--app-card-bg)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
          >
            {[
              {
                icon: '➕',
                iconBg: 'rgba(59,130,246,0.10)',
                label: t('taskList.explore_items.addProduct'),
                labelColor: 'var(--tg-theme-button-color)',
                sub: t('taskList.explore_items.addProductSub'),
                to: '/free-submit',
              },
              {
                icon: '🏪',
                iconBg: 'rgba(59,130,246,0.10)',
                label: t('taskList.explore_items.browseShops'),
                labelColor: 'var(--tg-theme-link-color)',
                sub: t('taskList.explore_items.browseShopsSub'),
                to: '/shops',
              },
              {
                icon: '📋',
                iconBg: 'rgba(156,163,175,0.12)',
                label: t('taskList.explore_items.mySubmissions'),
                labelColor: 'var(--tg-theme-text-color)',
                sub: t('taskList.explore_items.mySubmissionsSub'),
                to: '/submissions',
              },
            ].map((item, i, arr) => (
              <button
                key={item.to}
                className="w-full p-4 flex items-center gap-3 active:opacity-70 transition-opacity text-left"
                style={{
                  borderBottom: i < arr.length - 1
                    ? '1px solid var(--app-page-bg)'
                    : 'none',
                }}
                onClick={() => navigate(item.to)}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: item.iconBg }}
                >
                  {item.icon}
                </div>
                <div>
                  <p
                    className="font-semibold text-sm"
                    style={{ color: item.labelColor }}
                  >
                    {item.label}
                  </p>
                  <p
                    className="text-xs opacity-60 mt-0.5"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {item.sub}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Tasks section header + tab toggle ─────────────── */}
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg" style={{ color: 'var(--tg-theme-text-color)' }}>
            {tab === 'all' ? t('taskList.tasks') : t('taskList.myTasks')}
          </h2>
          <button
            className="text-sm font-medium"
            style={{ color: 'var(--tg-theme-link-color)' }}
            onClick={() => setTab(tab === 'all' ? 'mine' : 'all')}
          >
            {tab === 'all'
              ? `${t('taskList.myTasks')}${activeAssignments.length ? ` (${activeAssignments.length})` : ''}`
              : t('taskList.tasks')}
          </button>
        </div>

        {isLoading && <Spinner />}

        {/* ── All tasks view: active first, then available ───── */}
        {tab === 'all' && !isLoading && (
          <div className="space-y-3">
            {activeAssignments.length > 0 && (
              <>
                <p
                  className="text-xs font-medium uppercase opacity-40"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {t('taskList.inProgress')}
                </p>
                {activeAssignments.map((a) => (
                  <TaskCard
                    key={a.id}
                    task={a.task}
                    assignment={a}
                    submittedCount={a.task.items.filter((item) => submissions[item.id]).length}
                  />
                ))}
                {filteredAvailableTasks.length > 0 && (
                  <p
                    className="text-xs font-medium uppercase opacity-40 pt-1"
                    style={{ color: 'var(--tg-theme-text-color)' }}
                  >
                    {t('taskList.available')}
                  </p>
                )}
              </>
            )}

            {filteredAvailableTasks.length === 0 && activeAssignments.length === 0 && (
              <p
                className="text-center py-8 opacity-50 text-sm"
                style={{ color: 'var(--tg-theme-text-color)' }}
              >
                {t('taskList.noTasksAvailable')}
              </p>
            )}

            {filteredAvailableTasks.map((task) => {
              const spotsLeft = task.max_claims - task.current_claims
              const isAlmostFull = spotsLeft === 1 && task.max_claims > 1
              const isNew =
                Date.now() - new Date(task.created_at).getTime() < 48 * 60 * 60 * 1000
              return (
                <div key={task.id} className="relative">
                  {isAlmostFull && (
                    <span
                      className="absolute top-2 right-14 z-10 text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: '#f59e0b22', color: '#f59e0b' }}
                    >
                      {t('taskList.spotLeft')}
                    </span>
                  )}
                  <TaskCard task={task} isNew={isNew} />
                </div>
              )
            })}
          </div>
        )}

        {/* ── My Tasks tab ──────────────────────────────────── */}
        {tab === 'mine' && !isLoading && (
          <div className="space-y-3">
            {activeAssignments.length === 0 && completedAssignments.length === 0 && (
              <p
                className="text-center py-8 opacity-50 text-sm"
                style={{ color: 'var(--tg-theme-text-color)' }}
              >
                {t('taskList.noTasksClaimed')}
              </p>
            )}
            {activeAssignments.map((a) => (
              <TaskCard
                key={a.id}
                task={a.task}
                assignment={a}
                submittedCount={a.task.items.filter((item) => submissions[item.id]).length}
              />
            ))}
            {completedAssignments.length > 0 && (
              <>
                <p
                  className="text-xs font-medium uppercase opacity-40 pt-2"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {t('common:status.completed', { ns: 'common' })}
                </p>
                {completedAssignments.map((a) => (
                  <TaskCard key={a.id} task={a.task} assignment={a} />
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Your Progress ─────────────────────────────────── */}
        {tab === 'all' && (
          <div>
            <h2 className="font-bold text-lg mb-3" style={{ color: 'var(--tg-theme-text-color)' }}>
              {t('taskList.yourProgress')}
            </h2>
            <div
              className="rounded-2xl p-4"
              style={{ background: 'var(--app-card-bg)' }}
            >
              {/* Circle stepper + labels */}
              <div className="flex items-start">
                {[1, 2, 3, 4, 5].map((level, i) => {
                  const isAchieved = (stats?.level ?? 0) >= level
                  const isCurrent = (stats?.level ?? 0) === level
                  return (
                    <Fragment key={level}>
                      {i > 0 && (
                        <div
                          className="flex-1 h-0.5 mt-[18px] flex-shrink"
                          style={{
                            background: isAchieved
                              ? 'var(--tg-theme-button-color)'
                              : 'var(--app-page-bg)',
                          }}
                        />
                      )}
                      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 36 }}>
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
                          style={{
                            background: isAchieved
                              ? 'var(--tg-theme-button-color)'
                              : 'var(--app-page-bg)',
                            color: isAchieved ? 'white' : 'var(--tg-theme-hint-color)',
                          }}
                        >
                          {level}
                        </div>
                        <span
                          className="text-[10px] leading-tight mt-1 text-center"
                          style={{
                            color: isCurrent
                              ? 'var(--tg-theme-text-color)'
                              : 'var(--tg-theme-hint-color)',
                            fontWeight: isCurrent ? 700 : 400,
                            width: 52,
                            display: 'block',
                          }}
                        >
                          {t(`levels.${level}.name`, { ns: 'gamification' })}
                        </span>
                      </div>
                    </Fragment>
                  )
                })}
              </div>

              {/* XP progress bar within current level */}
              {stats && stats.next_level_xp != null && (
                <div
                  className="mt-3 h-1 rounded-full overflow-hidden"
                  style={{ background: 'var(--app-page-bg)' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${xpProgress * 100}%`,
                      background: 'var(--tg-theme-button-color)',
                    }}
                  />
                </div>
              )}

              {/* XP hint */}
              {stats && (
                <p className="text-xs mt-2" style={{ color: 'var(--tg-theme-hint-color)' }}>
                  {t('taskList.xpToNext', {
                    current: xpInLevel,
                    needed: xpNeeded > 0 ? xpNeeded : '∞',
                    nextLevel: nextLevelName,
                  })}
                  {stats.level < 2 && (
                    <span style={{ color: 'var(--tg-theme-link-color)' }}>
                      {t('taskList.unlockSpot')}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
